import { Location } from '@angular/common';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getFileTypeIconClass } from '@iupics-components/standard/preview-doc/preview-doc.component';
import { IupicsMenuType } from '@web-desktop/models/menu-item-ui';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, debounceTime, EMPTY, Observable, of, Subject, Subscription, switchMap, tap } from 'rxjs';
import { AuditFormat } from '../audit-window-ui/audit-format';
import SpecificWindowUiComponent from '../specific-window-ui/specific-window-ui.component';
import { DocumentDetailComponent } from './document-detail.component';
import {
  activeFilters,
  clearFilter,
  COMMON_EXTENSIONS,
  defaultState,
  DocFilterKey,
  DocState,
  FOLDERS,
  formatSize,
  fromQueryParams,
  PAGE_SIZES,
  SORTS,
  TAG_OPS,
  isComplete,
  OCR_STATUSES,
  tagName,
  toQueryBody,
  toQueryParams,
} from './documents-query';
import {
  DocBulkResult,
  DocDetail,
  DocFolderKind,
  DocFolders,
  DocItem,
  DocPage,
  DocSort,
  DocTag,
  DocTagFilter,
  DocTagRename,
  DocTagValue,
  DocTarget,
  DocUpload,
  DocUploadOptions,
  DocSavedView,
  DocVersion,
} from './documents.models';
import { DocActionResult, DocumentActionDialogComponent } from './document-action-dialog.component';
import { DocumentFilesService } from './document-files.service';
import { DocumentsLaunchService } from './documents-launch.service';
import { DocumentThumbComponent } from './document-thumb.component';
import { DocumentUploadDialogComponent } from './document-upload-dialog.component';
import { DocumentTagManagerComponent } from './document-tag-manager.component';
import { DocumentsService } from './documents.service';

type LoadState = 'loading' | 'ready' | 'error';

/** Notice of the status bar (polite live region); undo for reversible actions */
interface Notice {
  text: string;
  kind: 'info' | 'success' | 'error';
  undo?: () => void;
}

/** Files below this size are downloaded as a blob (authenticated call); bigger ones through a short-lived link */
const BLOB_DOWNLOAD_MAX = 25 * 1024 * 1024;
/** Uploads running at the same time */
const UPLOAD_CONCURRENCY = 2;

/**
 * Documents window (AD_Form "Document_Explorer"): explorer of the doc server documents the role may see.
 * Virtual folders (quick views, types, tables), search in names and extracted text, list / tiles, preview and details,
 * drag and drop upload with a queue, rename, user tags, favourites, bin with undo and restore, zip download.
 * The state lives in the URL of the form (bookmark / share); files go through ws only (DocumentsWS).
 */
@Component({
  selector: 'iu-document-explorer-ui',
  standalone: true,
  imports: [
    TranslateModule,
    DocumentDetailComponent,
    DocumentThumbComponent,
    DocumentUploadDialogComponent,
    DocumentActionDialogComponent,
    DocumentTagManagerComponent,
  ],
  templateUrl: './document-explorer-ui.component.html',
  styleUrls: ['./document-explorer-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class DocumentExplorerUiComponent extends SpecificWindowUiComponent implements OnInit, OnDestroy {
  readonly #docs = inject(DocumentsService);
  readonly #location = inject(Location);
  readonly #destroyRef = inject(DestroyRef);
  readonly #cd = inject(ChangeDetectorRef);
  #refreshQueued = false;
  #destroyed = false;

  @ViewChild('fileInput', { static: false }) fileInput: ElementRef<HTMLInputElement>;
  @ViewChild('searchInput', { static: false }) searchInput: ElementRef<HTMLInputElement>;

  readonly locale = AuditFormat.locale(this.connectorService.getIupicsUserAccount()?.default_language?.iso_code ?? this.translateService.currentLang);
  readonly fmt = signal(new AuditFormat(this.translateService.currentLang));
  readonly folderKinds = FOLDERS;
  readonly sorts = SORTS;
  readonly pageSizes = PAGE_SIZES;
  readonly extensions = COMMON_EXTENSIONS;

  readonly state = signal<DocState>(defaultState());
  readonly folders = signal<DocFolders | null>(null);
  readonly foldersState = signal<LoadState>('loading');
  readonly page = signal<DocPage | null>(null);
  readonly listState = signal<LoadState>('loading');
  readonly listError = signal<string | null>(null);

  /** selection (checkboxes, shift / ctrl click) and keyboard cursor */
  readonly selection = signal<Set<string>>(new Set());
  readonly cursor = signal<number>(-1);
  #anchor = -1;

  /** detail panel */
  readonly detail = signal<DocDetail | null>(null);
  readonly detailLoading = signal(false);
  readonly detailError = signal<string | null>(null);
  readonly busy = signal(false);

  /** upload queue */
  readonly uploads = signal<DocUpload[]>([]);
  readonly uploadsOpen = signal(true);
  readonly allRoles = signal(false);
  readonly dragging = signal(false);
  #dragDepth = 0;
  #uploadSeq = 0;
  readonly #uploadSubs = new Map<number, Subscription>();

  /** files waiting for the upload dialog */
  readonly pendingFiles = signal<File[] | null>(null);
  /** link / tag dialog and the documents it applies to */
  readonly action = signal<{ kind: 'link' | 'tags'; items: DocItem[]; target: DocTarget | null; canUnlink: boolean } | null>(null);
  readonly tagDefs = signal<DocTag[]>([]);
  readonly views = signal<DocSavedView[]>([]);
  readonly savingView = signal(false);
  /** new version being sent (progress %) */
  readonly versionProgress = signal<number | null>(null);
  //#region tag search
  readonly tagOps = TAG_OPS;
  /** tags a search can use */
  readonly tagKeys = signal<DocTag[]>([]);
  /** rows of the tag editor (may hold conditions not complete yet) */
  readonly tagRows = signal<DocTagFilter[]>([]);
  /** suggested values per row */
  readonly tagSuggestions = signal<Record<number, DocTagValue[]>>({});
  readonly #tagApply$ = new Subject<void>();
  readonly #tagSuggest$ = new Subject<{ index: number; key: string; q: string }>();
  #applyingTags = false;

  /** display name of a tag (names have no prefix: the server adds it) */
  tagKeyLabel(key: string): string {
    const name = tagName(key);
    if (name === 'DESCRIPTION') return this.translateService.instant('documents.tags.description');
    if (name === 'KEYWORDS') return this.translateService.instant('documents.tags.keywords');
    return this.tagKeys().find((k) => k.key === name)?.label ?? name.replace(/\$$/, '');
  }

  readonly ocrStatuses = OCR_STATUSES;

  setSize(which: 'sizeMin' | 'sizeMax', value: string): void {
    const n = parseFloat(value);
    this.patch(which === 'sizeMin' ? { sizeMin: n > 0 ? n : null } : { sizeMax: n > 0 ? n : null });
  }

  setUpdated(which: 'from' | 'to', value: string): void {
    const v = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
    const s = this.state();
    const from = which === 'from' ? v : s.updatedFrom;
    const to = which === 'to' ? v : s.updatedTo;
    if (from && to && from > to) this.patch({ updatedFrom: to, updatedTo: from });
    else this.patch(which === 'from' ? { updatedFrom: v } : { updatedTo: v });
  }

  /** chip text of a tag condition */
  tagChip(filterKey: string): string {
    const t = this.state().tags[Number(filterKey.slice(4))];
    if (!t) return '';
    const op = this.translateService.instant(`documents.tagSearch.ops.${t.op}`);
    // any of the conditions: the next ones read "or ..."
    const or = this.state().tagAny && this.state().tags.findIndex((x) => isComplete(x)) < Number(filterKey.slice(4))
      ? this.translateService.instant('documents.tagSearch.or') + ' '
      : '';
    return or + (t.op === 'set' || t.op === 'unset' ? `${this.tagKeyLabel(t.key)} ${op}` : `${this.tagKeyLabel(t.key)} ${op} "${t.value}"`);
  }

  addTagRow(): void {
    this.tagRows.update((rows) => [...rows, { key: '', op: 'contains', value: '' }]);
  }

  removeTagRow(index: number): void {
    this.tagRows.update((rows) => rows.filter((_, i) => i !== index));
    this.#applyTags();
  }

  setTagRow(index: number, change: Partial<DocTagFilter>): void {
    this.tagRows.update((rows) => rows.map((r, i) => (i === index ? { ...r, ...change } : r)));
    this.#applyTags();
  }

  onTagValue(index: number, value: string): void {
    this.tagRows.update((rows) => rows.map((r, i) => (i === index ? { ...r, value } : r)));
    this.suggest(index, value);
    this.#tagApply$.next();
  }

  suggest(index: number, q: string): void {
    const key = this.tagRows()[index]?.key;
    if (key) this.#tagSuggest$.next({ index, key, q });
  }

  /** the complete rows become the search (incomplete ones wait in the editor) */
  #applyTags(): void {
    const tags = this.tagRows().filter(isComplete).map((t) => ({ ...t, value: t.value.trim() }));
    const current = this.state().tags;
    if (JSON.stringify(tags) === JSON.stringify(current)) return;
    this.#applyingTags = true;
    try {
      this.patch({ tags });
    } finally {
      this.#applyingTags = false;
    }
  }
  //#endregion

  readonly #files = inject(DocumentFilesService);
  readonly #launch = inject(DocumentsLaunchService);
  #stopLaunch: (() => void) | null = null;

  readonly notice = signal<Notice | null>(null);
  #noticeTimer: ReturnType<typeof setTimeout> | undefined;
  /** navigation shown by default on wide screens */
  readonly navOpen = signal(typeof window === 'undefined' || window.innerWidth > 800);
  readonly tableFilter = signal('');
  readonly filtersOpen = signal(false);
  readonly confirmPurge = signal(false);

  readonly items = computed(() => this.page()?.items ?? []);
  readonly selectedItem = computed(() => {
    const id = this.state().doc;
    return id ? (this.items().find((i) => i.docId === id) ?? this.detail()?.item ?? null) : null;
  });
  readonly detailOpen = computed(() => !!this.state().doc);
  readonly selectedItems = computed(() => this.items().filter((i) => this.selection().has(i.docId)));
  readonly allSelected = computed(() => this.items().length > 0 && this.items().every((i) => this.selection().has(i.docId)));
  readonly inBin = computed(() => this.state().folder === 'trash');
  readonly canWrite = computed(() => !!this.folders()?.canWrite);
  readonly activeKeys = computed(() => activeFilters(this.state()));
  readonly pageInfo = computed(() => {
    const p = this.page();
    if (!p || p.total === 0) return null;
    return { from: p.offset + 1, to: Math.min(p.offset + p.items.length, p.total), total: p.total };
  });
  readonly hasNext = computed(() => {
    const p = this.page();
    return !!p && p.offset + p.items.length < p.total;
  });
  readonly tableFolders = computed(() => {
    const f = this.tableFilter().trim().toLowerCase();
    const all = this.folders()?.tables ?? [];
    return f ? all.filter((t) => t.label.toLowerCase().includes(f) || (t.tableName ?? '').toLowerCase().includes(f)) : all;
  });
  readonly tableLabel = computed(() => {
    const s = this.state();
    if (!s.tableId) return null;
    const t = this.folders()?.tables.find((x) => x.value === String(s.tableId));
    return t?.label ?? this.items().find((i) => i.tableId === s.tableId)?.tableLabel ?? `#${s.tableId}`;
  });
  readonly recordLabel = computed(() => {
    const s = this.state();
    if (!s.recordId) return null;
    return this.items().find((i) => i.recordId === s.recordId && i.tableId === s.tableId)?.recordLabel ?? `#${s.recordId}`;
  });
  /** upload target: a record when one is open, else none */
  /** record of the folder shown: proposed as the link of uploaded files */
  readonly uploadTarget = computed<DocTarget | null>(() => {
    const s = this.state();
    return s.tableId && s.recordId ? { tableId: s.tableId, tableLabel: this.tableLabel(), recordId: s.recordId, recordLabel: this.recordLabel() } : null;
  });
  readonly uploadSummary = computed(() => {
    const list = this.uploads();
    const done = list.filter((u) => u.state === 'done').length;
    const failed = list.filter((u) => u.state === 'error').length;
    const active = list.filter((u) => u.state === 'uploading' || u.state === 'waiting').length;
    return { total: list.length, done, failed, active };
  });
  readonly #load$ = new Subject<{ silent?: boolean }>();
  readonly #search$ = new Subject<string>();
  readonly #detail$ = new Subject<{ docId: string; silent?: boolean }>();
  readonly #folders$ = new Subject<void>();

  size = (n: number | null | undefined) => formatSize(n, this.locale);
  icon = (name: string) => getFileTypeIconClass(name);

  constructor() {
    super();
    this.#load$
      .pipe(
        switchMap(({ silent }) => {
          if (!silent) this.listState.set('loading');
          return this.#docs.search(toQueryBody(this.state())).pipe(
            tap((p) => {
              this.page.set(p);
              this.listState.set('ready');
              this.listError.set(null);
              // keep only the selected rows still listed
              const ids = new Set(p.items.map((i) => i.docId));
              this.selection.update((sel) => new Set([...sel].filter((id) => ids.has(id))));
              if (this.cursor() >= p.items.length) this.cursor.set(p.items.length - 1);
              // a page past the end (old link, fewer results since): last page
              const s = this.state();
              if (p.items.length === 0 && p.total > 0 && s.page > 0) setTimeout(() => this.setPage(Math.floor((p.total - 1) / s.pageSize)));
            }),
            catchError((e) => {
              if (!silent || this.listState() !== 'ready') {
                this.listState.set('error');
                this.listError.set(this.#message(e));
              }
              return of(null);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe(() => this.cdr());

    this.#folders$
      .pipe(
        debounceTime(300),
        switchMap(() =>
          this.#docs.folders().pipe(
            tap((f) => {
              this.folders.set(f);
              this.foldersState.set('ready');
            }),
            catchError(() => {
              if (!this.folders()) this.foldersState.set('error');
              return of(null);
            })
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => this.cdr());

    this.#tagApply$.pipe(debounceTime(400), takeUntilDestroyed()).subscribe(() => this.#applyTags());
    this.#tagSuggest$
      .pipe(
        debounceTime(250),
        switchMap(({ index, key, q }) =>
          this.#docs.tagValues(key, q.trim()).pipe(
            tap((values) => this.tagSuggestions.update((s) => ({ ...s, [index]: values }))),
            catchError(() => of(null))
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => this.cdr());

    this.#search$.pipe(debounceTime(350), takeUntilDestroyed()).subscribe((q) => {
      const s = this.state();
      // relevance first when words are searched in the content
      this.patch({ q, sort: q.trim() && !s.nameOnly ? (s.sort ?? null) : s.sort === 'relevance' ? null : s.sort });
    });

    this.#detail$
      .pipe(
        switchMap(({ docId, silent }) => {
          if (!silent) {
            this.detailLoading.set(true);
            this.detailError.set(null);
            this.detail.set(null);
          }
          return this.#docs.detail(docId, this.state().q.trim() || null).pipe(
            tap((d) => {
              if (this.state().doc === docId) this.detail.set(d);
              this.detailLoading.set(false);
            }),
            catchError((e) => {
              this.detailLoading.set(false);
              if (!silent) this.detailError.set(this.#message(e));
              return EMPTY;
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe(() => this.cdr());
  }

  override ngOnInit(): void {
    // custom screen: no form fields to build (the base ngOnInit would create the form store and build them)
    if (!this.isModal && this.activeTab) {
      this.workspaceService.addLinkedComponentToTabMap(this.activeTab.id, this);
    }
    this.state.set(fromQueryParams(new URLSearchParams(window.location.search)));
    // opened from a record window ("Documents" button): its documents
    const launch = this.#launch.take();
    if (launch) this.state.set({ ...defaultState(), tableId: launch.tableId, recordId: launch.recordId });
    this.#stopLaunch = this.#launch.listen((l) => {
      this.selection.set(new Set());
      this.patch({ ...defaultState(), view: this.state().view, tableId: l.tableId, recordId: l.recordId });
    });
    this.#folders$.next();
    this.#load$.next({});
    this.#loadViews();
    this.tagRows.set(this.state().tags.map((t) => ({ ...t })));
    this.#loadTagKeys();
    this.#docs
      .tags()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({ next: (t) => this.tagDefs.set(t), error: () => undefined });
    const doc = this.state().doc;
    if (doc) this.#detail$.next({ docId: doc });
  }

  override ngAfterViewInit(): void {
    // nothing to wire: the base one only registers the tab (done in ngOnInit)
  }

  override ngOnDestroy(): void {
    this.#destroyed = true;
    this.#stopLaunch?.();
    clearTimeout(this.#noticeTimer);
    for (const sub of this.#uploadSubs.values()) sub.unsubscribe();
    this.#uploadSubs.clear();
    super.ngOnDestroy();
  }

  /** the base builds the AD_FormDetail fields: this screen has none */
  override buildWindow(): void {}

  //#region navigation and filters
  patch(change: Partial<DocState>, resetPage = true): void {
    // tag conditions changed from outside the editor (chip, saved search, clear): the editor shows them
    if (change.tags && !this.#applyingTags) this.tagRows.set(change.tags.map((t) => ({ ...t })));
    this.state.update((s) => ({ ...s, ...change, page: resetPage && change.page === undefined ? 0 : (change.page ?? s.page) }));
    this.confirmPurge.set(false);
    this.#syncUrl();
    this.#load$.next({});
  }

  openFolder(folder: DocFolderKind): void {
    this.selection.set(new Set());
    this.cursor.set(-1);
    this.patch({ folder, type: null, tableId: null, recordId: null, doc: null });
  }

  openType(type: string): void {
    this.selection.set(new Set());
    this.patch({ folder: 'all', type, tableId: null, recordId: null, doc: null });
  }

  openTable(tableId: number): void {
    this.selection.set(new Set());
    this.patch({ folder: 'all', type: null, tableId, recordId: null, doc: null });
  }

  /** documents of the record of a document */
  openRecordFolder(item: DocItem): void {
    if (!item.tableId || !item.recordId) return;
    this.selection.set(new Set());
    this.patch({ folder: 'all', type: null, tableId: item.tableId, recordId: item.recordId, doc: null });
  }

  isFolderActive(folder: DocFolderKind): boolean {
    const s = this.state();
    return s.folder === folder && (folder !== 'all' || (!s.type && !s.tableId));
  }

  folderCount(folder: DocFolderKind): number | null {
    const f = this.folders();
    if (!f) return null;
    switch (folder) {
      case 'all':
        return f.total;
      case 'mine':
        return f.mine;
      case 'recent':
        return f.recent;
      case 'favourites':
        return f.favourites;
      case 'trash':
        return f.trash;
    }
  }

  folderIcon(folder: DocFolderKind): string {
    return {
      all: 'fa-solid fa-folder-tree',
      mine: 'fa-solid fa-user',
      recent: 'fa-solid fa-clock-rotate-left',
      favourites: 'fa-solid fa-star',
      trash: 'fa-solid fa-trash-can',
    }[folder];
  }

  typeLabel(type: string | null): string {
    const key = `documents.types.${type ?? 'OTHER'}`;
    const label = this.translateService.instant(key);
    return label === key ? (type ?? '') : label;
  }

  onSearchInput(value: string): void {
    this.#search$.next(value);
  }

  setNameOnly(nameOnly: boolean): void {
    this.patch({ nameOnly, sort: nameOnly && this.state().sort === 'relevance' ? null : this.state().sort });
  }

  setSort(sort: DocSort): void {
    const s = this.state();
    // same column: other direction
    if ((s.sort ?? 'date') === sort) this.patch({ sort, asc: !(s.asc ?? (sort === 'name' || sort === 'type')) }, false);
    else this.patch({ sort, asc: null }, false);
  }

  sortDir(sort: DocSort): 'asc' | 'desc' | null {
    const s = this.state();
    const current = s.sort ?? (s.q.trim() && !s.nameOnly ? 'relevance' : 'date');
    if (current !== sort) return null;
    return (s.asc ?? (sort === 'name' || sort === 'type')) ? 'asc' : 'desc';
  }

  ariaSort(sort: DocSort): string {
    const d = this.sortDir(sort);
    return d === 'asc' ? 'ascending' : d === 'desc' ? 'descending' : 'none';
  }

  setPage(page: number): void {
    this.selection.set(new Set());
    this.patch({ page: Math.max(0, page) }, false);
  }

  setPageSize(pageSize: number): void {
    this.patch({ pageSize });
  }

  setView(view: 'list' | 'tiles'): void {
    this.state.update((s) => ({ ...s, view }));
    this.#syncUrl();
  }

  removeFilter(key: DocFilterKey): void {
    if (key === 'q' && this.searchInput) this.searchInput.nativeElement.value = '';
    this.patch(clearFilter(this.state(), key));
  }

  clearFilters(): void {
    if (this.searchInput) this.searchInput.nativeElement.value = '';
    this.patch({
      type: null,
      tableId: null,
      recordId: null,
      q: '',
      nameOnly: false,
      ext: null,
      from: null,
      to: null,
      tags: [],
      tagAny: false,
      sizeMin: null,
      sizeMax: null,
      ocr: null,
      versioned: false,
      updatedFrom: null,
      updatedTo: null,
      sort: null,
      asc: null,
    });
  }

  setDay(which: 'from' | 'to', value: string): void {
    const v = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
    const s = this.state();
    const from = which === 'from' ? v : s.from;
    const to = which === 'to' ? v : s.to;
    // an inverted period is swapped rather than refused
    if (from && to && from > to) this.patch({ from: to, to: from });
    else this.patch(which === 'from' ? { from: v } : { to: v });
  }

  filterByTag(key: string, value: string): void {
    const tags = this.state().tags;
    if (tags.some((t) => t.key === key && t.op === 'contains' && t.value === value)) return;
    this.patch({ tags: [...tags, { key, op: 'contains', value }] });
  }

  reload(): void {
    this.#folders$.next();
    this.#load$.next({});
    const doc = this.state().doc;
    if (doc) this.#detail$.next({ docId: doc, silent: true });
  }

  /** filters in the URL query string of this form (bookmark / share); only while this form owns the URL */
  #syncUrl(): void {
    const path = this.#location.path().split('?')[0];
    if (!path.startsWith('/workspace/form/')) return;
    this.#location.replaceState(path, toQueryParams(this.state()).toString());
  }
  //#endregion

  //#region selection and keyboard
  isSelected(item: DocItem): boolean {
    return this.selection().has(item.docId);
  }

  toggleSelect(item: DocItem, index: number, event?: MouseEvent | KeyboardEvent): void {
    const sel = new Set(this.selection());
    if (event?.shiftKey && this.#anchor >= 0) {
      const [a, b] = [Math.min(this.#anchor, index), Math.max(this.#anchor, index)];
      for (const it of this.items().slice(a, b + 1)) sel.add(it.docId);
    } else if (sel.has(item.docId)) sel.delete(item.docId);
    else sel.add(item.docId);
    this.#anchor = index;
    this.selection.set(sel);
  }

  toggleAll(): void {
    this.selection.set(this.allSelected() ? new Set() : new Set(this.items().map((i) => i.docId)));
  }

  clearSelection(): void {
    this.selection.set(new Set());
  }

  /** click on a row: open it; ctrl / shift click: select */
  onRowClick(item: DocItem, index: number, event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('button, a, input, label')) return;
    this.cursor.set(index);
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      this.toggleSelect(item, index, event);
      return;
    }
    this.#anchor = index;
    this.openDetail(item);
  }

  onRowDblClick(item: DocItem): void {
    if (item.link) window.open(item.link, '_blank', 'noopener');
    else this.openInTab(item);
  }

  /** list keyboard: arrows / Home / End move, Enter opens, Space selects, Delete bins, F2 renames, Ctrl+A selects all */
  onListKey(event: KeyboardEvent): void {
    const items = this.items();
    if (!items.length) return;
    const tiles = this.state().view === 'tiles';
    const step = tiles ? this.#tilesPerRow() : 1;
    let i = this.cursor();
    const target = event.target as HTMLElement;
    if (target.closest('input, textarea, select')) return;
    switch (event.key) {
      case 'ArrowDown':
        i = Math.min(items.length - 1, i < 0 ? 0 : i + step);
        break;
      case 'ArrowUp':
        i = Math.max(0, i < 0 ? 0 : i - step);
        break;
      case 'ArrowRight':
        if (!tiles) return;
        i = Math.min(items.length - 1, i + 1);
        break;
      case 'ArrowLeft':
        if (!tiles) return;
        i = Math.max(0, i - 1);
        break;
      case 'Home':
        i = 0;
        break;
      case 'End':
        i = items.length - 1;
        break;
      case 'Enter':
        if (i >= 0) this.openDetail(items[i]);
        event.preventDefault();
        return;
      case ' ':
        if (i >= 0) this.toggleSelect(items[i], i, event);
        event.preventDefault();
        return;
      case 'Delete':
        this.#deleteKey();
        event.preventDefault();
        return;
      case 'F2':
        if (i >= 0 && items[i].editable) {
          this.openDetail(items[i]);
          setTimeout(() => (this.specificContainer?.nativeElement as HTMLElement)?.querySelector<HTMLButtonElement>('.doc-detail .fa-pen')?.closest('button')?.click(), 50);
        }
        event.preventDefault();
        return;
      case 'a':
        if (event.ctrlKey || event.metaKey) {
          this.toggleAll();
          event.preventDefault();
        }
        return;
      case 'Escape':
        if (this.selection().size) this.clearSelection();
        else if (this.detailOpen()) this.closeDetail();
        return;
      default:
        return;
    }
    event.preventDefault();
    this.cursor.set(i);
    if (event.shiftKey) this.toggleSelect(items[i], i, event);
    setTimeout(() => (this.specificContainer?.nativeElement as HTMLElement)?.querySelector<HTMLElement>(`[data-doc-index="${i}"]`)?.focus());
  }

  #tilesPerRow(): number {
    const grid = (this.specificContainer?.nativeElement as HTMLElement)?.querySelector<HTMLElement>('.doc-tiles');
    if (!grid) return 1;
    const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').filter((c) => c.trim()).length;
    return Math.max(1, columns);
  }

  /** global shortcut: "/" focuses the search */
  onWindowKey(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (event.key === '/' && !target.closest('input, textarea, select, [contenteditable]')) {
      event.preventDefault();
      this.searchInput?.nativeElement.focus();
    }
  }

  #deleteKey(): void {
    const targets = this.selectedItems().length ? this.selectedItems() : this.selectedItem() ? [this.selectedItem()!] : [];
    if (!targets.length) return;
    if (this.inBin()) this.purgeItems(targets);
    else this.trashItems(targets);
  }
  //#endregion

  //#region detail
  openDetail(item: DocItem): void {
    if (this.state().doc === item.docId) return;
    this.state.update((s) => ({ ...s, doc: item.docId }));
    this.#syncUrl();
    this.detail.set(null);
    this.#detail$.next({ docId: item.docId });
    // narrow screens: the panel covers the list, so the focus goes into it (Escape or close: back to the row)
    if (this.#overlayDetail()) {
      this.#detailOpener = document.activeElement as HTMLElement | null;
      setTimeout(() => this.#root()?.querySelector<HTMLElement>('.doc-detail .doc-close')?.focus());
    }
  }

  closeDetail(): void {
    const root = this.#root();
    const focusInside = !!root?.querySelector('.doc-detail')?.contains(document.activeElement);
    this.state.update((s) => ({ ...s, doc: null }));
    this.#syncUrl();
    this.detail.set(null);
    this.detailError.set(null);
    const opener = this.#detailOpener;
    this.#detailOpener = null;
    if (focusInside) setTimeout(() => (opener?.isConnected ? opener : root?.querySelector<HTMLElement>('[data-doc-index]'))?.focus());
  }

  #detailOpener: HTMLElement | null = null;

  #root(): HTMLElement | null {
    return (this.specificContainer?.nativeElement as HTMLElement | undefined) ?? null;
  }

  /** the detail panel is shown over the list (see the 800px breakpoint of the stylesheet) */
  #overlayDetail(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 800px)').matches;
  }

  retryDetail(): void {
    const doc = this.state().doc;
    if (doc) this.#detail$.next({ docId: doc });
  }

  /** zoom to the record in its window (same path as notifications: access checked, menu entry required) */
  openRecord(item: DocItem): void {
    if (!item.tableName || !item.recordId) return;
    this.uiCreator.zoomAcross(item.tableName, `${item.tableName}_ID`, item.recordId, true).subscribe({
      next: (dataWs) => {
        if (!dataWs?.length) {
          this.#notify(this.translateService.instant('documents.state.noRecordAccess'), 'error');
          return;
        }
        this.uiCreator.getIupicsMenus().subscribe((menus) => {
          const windowId = parseInt(dataWs[0].Window_ID, 10);
          const menu = menus.find((m) => m.actionID === windowId && m.menuType === IupicsMenuType.WINDOW);
          if (!menu) {
            this.#notify(this.translateService.instant('documents.state.noRecordAccess'), 'error');
            return;
          }
          this.workspaceService.openTargetSearchEmt.emit({
            zoomInfo: {
              windowId,
              dataUUID: dataWs[dataWs.length - 1]['Record_ID'],
              record_id: item.recordId,
              children: dataWs.length > 1 ? dataWs.slice(0, dataWs.length - 1) : null,
            },
            cat: { id: windowId },
            // the first tab opens the root record (last entry); the others (children) are opened down to the record
            source: { id: dataWs[dataWs.length - 1]?.Record_ID ?? 'newRecord' },
          });
        });
      },
      error: () => this.#notify(this.translateService.instant('documents.state.noRecordAccess'), 'error'),
    });
  }

  /** replace an item in the page and the detail after a change */
  #replace(item: DocItem): void {
    this.page.update((p) => (p ? { ...p, items: p.items.map((i) => (i.docId === item.docId ? item : i)) } : p));
    const d = this.detail();
    if (d && d.item.docId === item.docId) this.detail.set({ ...d, item });
  }

  rename(item: DocItem, fileName: string): void {
    this.busy.set(true);
    this.#docs
      .update(item.docId, { fileName })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (updated) => {
          this.busy.set(false);
          this.#replace(updated);
          this.#notify(this.translateService.instant('documents.notice.renamed', { name: updated.fileName }), 'success');
        },
        error: (e) => {
          this.busy.set(false);
          this.#notify(this.#message(e), 'error');
        },
      });
  }

  saveTags(item: DocItem, tags: Record<string, string>): void {
    this.busy.set(true);
    this.#docs
      .update(item.docId, { tags })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (updated) => {
          this.busy.set(false);
          this.#replace(updated);
          this.#detail$.next({ docId: item.docId, silent: true });
          this.#notify(this.translateService.instant('documents.notice.tagsSaved'), 'success');
        },
        error: (e) => {
          this.busy.set(false);
          this.#notify(this.#message(e), 'error');
        },
      });
  }

  toggleFavourite(item: DocItem, favourite: boolean): void {
    this.#replace({ ...item, favourite });
    this.#docs
      .favourite(item.docId, favourite)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.#folders$.next();
          if (!favourite && this.state().folder === 'favourites') this.#load$.next({ silent: true });
        },
        error: (e) => {
          this.#replace(item);
          this.#notify(this.#message(e), 'error');
        },
      });
  }
  //#endregion

  //#region files
  download(item: DocItem): void {
    if (item.link) {
      window.open(item.link, '_blank', 'noopener');
      return;
    }
    if (item.size !== null && item.size <= BLOB_DOWNLOAD_MAX) {
      this.busy.set(true);
      this.#docs
        .content(item.docId)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: (blob) => {
            this.busy.set(false);
            this.#save(blob, item.fileName);
          },
          error: async (e) => {
            this.busy.set(false);
            this.#notify(this.#message(await this.#blobError(e)), 'error');
          },
        });
      return;
    }
    // big or unknown size: the browser downloads it (no memory copy), through a short-lived link
    this.#withLink(item, false, (url) => {
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      a.click();
    });
  }

  openInTab(item: DocItem): void {
    // the tab is opened now (popup blockers), its address is set when the link arrives
    const tab = window.open('about:blank', '_blank');
    this.#withLink(
      item,
      true,
      (url) => {
        if (tab) tab.location.href = url;
        else window.open(url, '_blank', 'noopener');
      },
      () => tab?.close()
    );
  }

  copyLink(item: DocItem): void {
    this.#withLink(item, false, (url, expiresAt) => {
      navigator.clipboard?.writeText(url).then(
        () => this.#notify(this.translateService.instant('documents.notice.linkCopied', { time: this.fmt().dateTime(expiresAt) }), 'success'),
        () => this.#notify(url, 'info')
      );
    });
  }

  #withLink(item: DocItem, inline: boolean, use: (url: string, expiresAt: string) => void, failed?: () => void): void {
    this.#docs
      .link(item.docId, inline)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (link) => use(this.#docs.wsRoot + link.url, link.expiresAt),
        error: (e) => {
          failed?.();
          this.#notify(this.#message(e), 'error');
        },
      });
  }

  downloadSelection(): void {
    const items = this.selectedItems().filter((i) => !i.link);
    if (!items.length) return;
    if (items.length === 1) {
      this.download(items[0]);
      return;
    }
    // the browser downloads the zip through a short-lived link: nothing is kept in memory
    this.busy.set(true);
    this.#docs
      .zipLink(items.map((i) => i.docId))
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (link) => {
          this.busy.set(false);
          const a = document.createElement('a');
          a.href = this.#docs.wsRoot + link.url;
          a.rel = 'noopener';
          a.click();
          this.#notify(this.translateService.instant('documents.notice.zipping', { count: items.length }), 'info');
        },
        error: (e) => {
          this.busy.set(false);
          this.#notify(this.#message(e), 'error');
        },
      });
  }

  #save(blob: Blob, name: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    this.cdr();
  }

  /** the error body of a blob request is a Blob: read it to get the message */
  async #blobError(e: unknown): Promise<unknown> {
    if (e instanceof HttpErrorResponse && e.error instanceof Blob) {
      try {
        return new HttpErrorResponse({ error: JSON.parse(await e.error.text()), status: e.status, statusText: e.statusText, url: e.url ?? undefined });
      } catch {
        return e;
      }
    }
    return e;
  }
  //#endregion

  //#region bin
  trashItems(items: DocItem[]): void {
    const ids = items.filter((i) => i.editable && !i.trashed).map((i) => i.docId);
    if (!ids.length) {
      this.#notify(this.translateService.instant('documents.notice.nothingToBin'), 'info');
      return;
    }
    this.#bulk(this.#docs.trash(ids), (r) => {
      const n = r.done.length;
      this.#notify(
        this.translateService.instant('documents.notice.binned', { count: n }) + this.#failures(r),
        r.failed.length ? 'error' : 'success',
        n ? () => this.restoreIds(r.done, true) : undefined
      );
    });
  }

  restoreItems(items: DocItem[]): void {
    this.restoreIds(items.filter((i) => i.trashed && i.editable).map((i) => i.docId), false);
  }

  restoreIds(ids: string[], undo: boolean): void {
    if (!ids.length) return;
    this.#bulk(this.#docs.restore(ids), (r) => {
      this.#notify(
        this.translateService.instant(undo ? 'documents.notice.undone' : 'documents.notice.restored', { count: r.done.length }) + this.#failures(r),
        r.failed.length ? 'error' : 'success'
      );
    });
  }

  /** delete permanently (from the bin): asked twice for a selection, once from the detail (it asks itself) */
  purgeItems(items: DocItem[], confirmed = false): void {
    const ids = items.filter((i) => i.trashed && i.editable).map((i) => i.docId);
    if (!ids.length) return;
    if (!confirmed && !this.confirmPurge()) {
      this.confirmPurge.set(true);
      this.#notify(this.translateService.instant('documents.notice.purgeAsk', { count: ids.length }), 'info');
      return;
    }
    this.confirmPurge.set(false);
    this.#bulk(this.#docs.purge(ids), (r) => {
      this.#notify(this.translateService.instant('documents.notice.purged', { count: r.done.length }) + this.#failures(r), r.failed.length ? 'error' : 'success');
    });
  }

  #bulk(call: Observable<DocBulkResult>, done: (r: DocBulkResult) => void): void {
    this.busy.set(true);
    call.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe({
      next: (r) => {
        this.busy.set(false);
        const gone = new Set(r.done);
        if (this.state().doc && gone.has(this.state().doc!)) this.closeDetail();
        this.selection.update((sel) => new Set([...sel].filter((id) => !gone.has(id))));
        done(r);
        this.#folders$.next();
        this.#load$.next({ silent: true });
      },
      error: (e) => {
        this.busy.set(false);
        this.#notify(this.#message(e), 'error');
      },
    });
  }

  #failures(r: DocBulkResult): string {
    if (!r.failed.length) return '';
    const reasons = [...new Set(r.failed.map((f) => this.translateService.instant(`documents.reasons.${f.reason}`)))];
    return ' ' + this.translateService.instant('documents.notice.failed', { count: r.failed.length, reasons: reasons.join(', ') });
  }
  //#endregion

  //#region upload
  pickFiles(): void {
    this.fileInput?.nativeElement.click();
  }

  onFilesPicked(input: HTMLInputElement): void {
    if (input.files?.length) this.enqueue(Array.from(input.files));
    input.value = '';
  }

  onDragEnter(event: DragEvent): void {
    if (!this.canWrite() || !this.#hasFiles(event)) return;
    event.preventDefault();
    this.#dragDepth++;
    this.dragging.set(true);
  }

  onDragOver(event: DragEvent): void {
    if (!this.canWrite() || !this.#hasFiles(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  onDragLeave(event: DragEvent): void {
    if (!this.dragging()) return;
    event.preventDefault();
    this.#dragDepth = Math.max(0, this.#dragDepth - 1);
    if (this.#dragDepth === 0) this.dragging.set(false);
  }

  onDrop(event: DragEvent): void {
    if (!this.canWrite() || !this.#hasFiles(event)) return;
    event.preventDefault();
    this.#dragDepth = 0;
    this.dragging.set(false);
    const files = Array.from(event.dataTransfer?.files ?? []).filter((f) => f.size > 0 || f.type);
    if (files.length) this.enqueue(files);
  }

  #hasFiles(event: DragEvent): boolean {
    return !!event.dataTransfer && Array.from(event.dataTransfer.types).includes('Files');
  }

  /** picked or dropped files: the upload dialog asks for the record, tags and visibility first */
  enqueue(files: File[]): void {
    if (!this.canWrite() || !files.length) return;
    this.pendingFiles.set(files);
    this.cdr();
  }

  startUploads(files: File[], options: DocUploadOptions): void {
    this.pendingFiles.set(null);
    this.allRoles.set(options.allRoles);
    const max = this.folders()?.maxUploadBytes ?? 0;
    const added: DocUpload[] = files.map((file) => ({
      id: ++this.#uploadSeq,
      file,
      state: max > 0 && file.size > max ? 'error' : 'waiting',
      progress: 0,
      error: max > 0 && file.size > max ? this.translateService.instant('documents.upload.tooBig', { max: this.size(max) }) : null,
      docId: null,
      uploadKey: this.#newKey(),
      options,
    }));
    this.uploads.update((u) => [...u, ...added]);
    this.uploadsOpen.set(true);
    this.#pump();
  }

  #pump(): void {
    const list = this.uploads();
    let running = list.filter((u) => u.state === 'uploading').length;
    for (const u of list) {
      if (running >= UPLOAD_CONCURRENCY) break;
      if (u.state !== 'waiting') continue;
      running++;
      this.#start(u);
    }
    this.cdr();
  }

  #setUpload(id: number, change: Partial<DocUpload>): void {
    this.uploads.update((list) => list.map((u) => (u.id === id ? { ...u, ...change } : u)));
  }

  #start(u: DocUpload): void {
    this.#setUpload(u.id, { state: 'uploading', progress: 0, error: null });
    const target = u.options.target;
    const sub = this.#docs
      .upload(u.file, target?.tableId ?? null, target?.recordId ?? null, u.options.allRoles, u.options.tags, u.uploadKey)
      .subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.#setUpload(u.id, { progress: event.total ? Math.round((event.loaded / event.total) * 100) : 0 });
          this.cdr();
        } else if (event.type === HttpEventType.Response) {
          this.#setUpload(u.id, { state: 'done', progress: 100, docId: event.body?.docId ?? null });
          this.#uploadSubs.delete(u.id);
          this.#afterUpload();
        }
      },
      error: (e) => {
        this.#setUpload(u.id, { state: 'error', error: this.#message(e) });
        this.#uploadSubs.delete(u.id);
        this.#pump();
      },
    });
    this.#uploadSubs.set(u.id, sub);
  }

  #afterUpload(): void {
    this.#pump();
    // refresh once the batch is over (or every few files of a long batch)
    const s = this.uploadSummary();
    if (s.active === 0 || s.done % 5 === 0) {
      this.#folders$.next();
      this.#load$.next({ silent: true });
    }
    if (s.active === 0 && s.failed === 0) this.#notify(this.translateService.instant('documents.notice.uploaded', { count: s.done }), 'success');
  }

  cancelUpload(u: DocUpload): void {
    const wasSent = u.state === 'uploading';
    this.#uploadSubs.get(u.id)?.unsubscribe();
    this.#uploadSubs.delete(u.id);
    this.#setUpload(u.id, { state: 'cancelled' });
    this.#pump();
    if (wasSent) {
      // the server may have stored it (or still be storing it): remove it now and once more a bit later
      const key = u.uploadKey;
      const remove = () =>
        this.#docs.cancelUpload(key).subscribe({
          next: (r) => {
            if (r.removed) {
              this.#folders$.next();
              this.#load$.next({ silent: true });
            }
          },
          error: () => undefined,
        });
      remove();
      setTimeout(remove, 5000);
      setTimeout(remove, 20000);
    }
  }

  retryUpload(u: DocUpload): void {
    // a new key: what the cancelled attempt may have stored is removed with the old one
    this.#setUpload(u.id, { state: 'waiting', error: null, progress: 0, uploadKey: this.#newKey() });
    this.#pump();
  }

  clearUploads(): void {
    this.uploads.update((list) => list.filter((u) => u.state === 'uploading' || u.state === 'waiting'));
  }

  openUploaded(u: DocUpload): void {
    if (!u.docId) return;
    const item = this.items().find((i) => i.docId === u.docId);
    if (item) this.openDetail(item);
    else {
      this.state.update((s) => ({ ...s, doc: u.docId }));
      this.#syncUrl();
      this.#detail$.next({ docId: u.docId });
    }
  }
  //#endregion

  #newKey(): string {
    return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}-${this.#uploadSeq}`;
  }

  //#region saved searches
  /** query string of the current search (folder, filters, sort; not the page nor the open document) */
  readonly currentQuery = computed(() => toQueryParams({ ...this.state(), page: 0, doc: null }).toString());

  #loadViews(): void {
    this.#docs
      .views()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (v) => {
          this.views.set(v);
          this.cdr();
        },
        error: () => undefined,
      });
  }

  applyView(v: DocSavedView): void {
    const next = fromQueryParams(new URLSearchParams(v.query));
    if (this.searchInput) this.searchInput.nativeElement.value = next.q;
    this.selection.set(new Set());
    this.patch({ ...next, doc: null, view: this.state().view });
  }

  startSaveView(): void {
    this.savingView.set(true);
    setTimeout(() => (this.specificContainer?.nativeElement as HTMLElement)?.querySelector<HTMLInputElement>('.docs-save-view input')?.focus());
  }

  saveView(name: string): void {
    const n = name.trim();
    if (!n) return;
    this.#docs
      .saveView(n, this.currentQuery())
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.savingView.set(false);
          this.#loadViews();
          this.#notify(this.translateService.instant('documents.views.saved', { name: n }), 'success');
        },
        error: (e) => this.#notify(this.#message(e), 'error'),
      });
  }

  deleteView(v: DocSavedView): void {
    this.#docs
      .deleteView(v.id)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.views.update((list) => list.filter((x) => x.id !== v.id));
          this.#notify(this.translateService.instant('documents.views.deleted', { name: v.name }), 'info', () =>
            this.#docs.saveView(v.name, v.query).subscribe(() => this.#loadViews())
          );
        },
        error: (e) => this.#notify(this.#message(e), 'error'),
      });
  }
  //#endregion

  //#region link to a record, tags of a selection
  openAction(kind: 'link' | 'tags', items: DocItem[]): void {
    const editable = items.filter((i) => i.editable && !i.trashed && (kind === 'tags' || i.type === 'DOCUMENT'));
    if (!editable.length) {
      this.#notify(this.translateService.instant(kind === 'link' ? 'documents.link.nothing' : 'documents.notice.nothingToTag'), 'info');
      return;
    }
    const first = editable[0];
    const target: DocTarget | null =
      kind === 'link' && editable.length === 1 && first.tableId && first.recordId
        ? { tableId: first.tableId, tableLabel: first.tableLabel, recordId: first.recordId, recordLabel: first.recordLabel }
        : null;
    this.action.set({ kind, items: editable, target, canUnlink: editable.some((i) => !!i.tableId) });
  }

  runAction(result: DocActionResult): void {
    const a = this.action();
    this.action.set(null);
    if (!a) return;
    const ids = a.items.map((i) => i.docId);
    if (result.kind === 'link') {
      const t = result.target;
      this.#bulk(this.#docs.linkRecord(ids, t?.tableId ?? null, t?.recordId ?? null), (r) => {
        this.#notify(
          this.translateService.instant(t ? 'documents.notice.linked' : 'documents.notice.unlinked', { count: r.done.length, record: t?.recordLabel ?? '' }) +
            this.#failures(r),
          r.failed.length ? 'error' : 'success'
        );
        this.#refreshDetail();
      });
    } else {
      this.#bulk(this.#docs.bulkTags(ids, result.key, result.value, result.mode), (r) => {
        this.#notify(this.translateService.instant('documents.notice.tagged', { count: r.done.length }) + this.#failures(r), r.failed.length ? 'error' : 'success');
        this.#refreshDetail();
      });
    }
  }

  //#region tag rename (administrators)
  readonly tagManagerOpen = signal(false);

  onTagRenamed(r: DocTagRename): void {
    this.tagManagerOpen.set(false);
    this.#notify(this.translateService.instant('documents.tagManager.done', { count: r.modified, from: r.from, to: r.to }), 'success');
    // the old name may sit in the filters: it follows the rename
    const tags = this.state().tags.map((t) => (tagName(t.key) === r.from ? { ...t, key: r.to } : t));
    this.tagRows.set(tags.map((t) => ({ ...t })));
    this.patch({ tags });
    this.#loadTagKeys();
    this.#refreshDetail();
  }

  #loadTagKeys(): void {
    this.#docs
      .tagKeys()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({ next: (k) => this.tagKeys.set(k), error: () => undefined });
  }
  //#endregion

  #refreshDetail(): void {
    const doc = this.state().doc;
    if (doc) this.#detail$.next({ docId: doc, silent: true });
  }
  //#endregion

  //#region text extraction, versions
  retryOcr(item: DocItem): void {
    this.#docs
      .retryExtraction(item.docId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.#notify(this.translateService.instant('documents.ocr.started'), 'info');
          this.#refreshDetail();
          // the extraction runs in the background: look again a bit later
          setTimeout(() => this.#refreshDetail(), 8000);
        },
        error: (e) => this.#notify(this.#message(e), 'error'),
      });
  }

  uploadVersion(item: DocItem, file: File): void {
    const max = this.folders()?.maxUploadBytes ?? 0;
    if (max > 0 && file.size > max) {
      this.#notify(this.translateService.instant('documents.upload.tooBig', { max: this.size(max) }), 'error');
      return;
    }
    this.versionProgress.set(0);
    this.#docs
      .uploadVersion(item.docId, file)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.versionProgress.set(event.total ? Math.round((event.loaded / event.total) * 100) : 0);
            this.cdr();
          } else if (event.type === HttpEventType.Response && event.body) {
            this.versionProgress.set(null);
            this.#files.forget(item.docId);
            this.#replace(event.body);
            this.#refreshDetail();
            this.#load$.next({ silent: true });
            this.#notify(this.translateService.instant('documents.versions.uploaded', { version: event.body.version }), 'success');
          }
        },
        error: (e) => {
          this.versionProgress.set(null);
          this.#notify(this.#message(e), 'error');
        },
      });
  }

  restoreVersion(item: DocItem, number: number): void {
    this.busy.set(true);
    this.#docs
      .restoreVersion(item.docId, number)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (updated) => {
          this.busy.set(false);
          this.#files.forget(item.docId);
          this.#replace(updated);
          this.#refreshDetail();
          this.#load$.next({ silent: true });
          this.#notify(this.translateService.instant('documents.versions.restored', { version: number }), 'success');
        },
        error: (e) => {
          this.busy.set(false);
          this.#notify(this.#message(e), 'error');
        },
      });
  }

  downloadVersion(item: DocItem, v: DocVersion): void {
    this.busy.set(true);
    this.#docs
      .versionContent(item.docId, v.version)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (blob) => {
          this.busy.set(false);
          this.#save(blob, v.fileName || item.fileName);
        },
        error: async (e) => {
          this.busy.set(false);
          this.#notify(this.#message(await this.#blobError(e)), 'error');
        },
      });
  }
  //#endregion

  #notify(text: string, kind: Notice['kind'], undo?: () => void): void {
    clearTimeout(this.#noticeTimer);
    this.notice.set({ text, kind, undo });
    this.#noticeTimer = setTimeout(() => {
      this.notice.set(null);
      this.cdr();
    }, undo ? 10000 : 6000);
    this.cdr();
  }

  runUndo(n: Notice): void {
    this.notice.set(null);
    n.undo?.();
  }

  dismissNotice(): void {
    clearTimeout(this.#noticeTimer);
    this.notice.set(null);
  }

  #message(e: unknown): string {
    if (e instanceof HttpErrorResponse) {
      if (e.status === 403 || (e.status === 500 && /access denied/i.test(JSON.stringify(e.error ?? '')))) {
        return this.translateService.instant('documents.state.forbidden');
      }
      if (e.status === 0) return this.translateService.instant('documents.state.offline');
      if (e.status === 404) return this.translateService.instant('documents.state.notFound');
      if (e.status === 409) return this.translateService.instant('documents.state.conflict');
      if (e.status === 413) return this.translateService.instant('documents.upload.tooBig', { max: this.size(this.folders()?.maxUploadBytes ?? 0) });
      if (e.status === 502 || e.status === 503) return this.translateService.instant('documents.state.serverDown');
      const msg = e.error?.message;
      if (e.status === 400 && msg) return msg;
    }
    return this.translateService.instant('documents.state.error');
  }

  trackItem = (_: number, i: DocItem) => i.docId;

  num(value: string | number): number {
    return Number(value);
  }

  /**
   * Updates the view: the specific window can sit in a view the application tick does not refresh after an
   * asynchronous answer (results only appeared on the next click), so the view is checked here, in a microtask.
   */
  private cdr(): void {
    this.#cd.markForCheck();
    if (this.#refreshQueued) return;
    this.#refreshQueued = true;
    queueMicrotask(() => {
      this.#refreshQueued = false;
      if (!this.#destroyed) this.#cd.detectChanges();
    });
  }
}
